/**
 * Forge › Sets (docs/design/ui-simplify.md §4.4c): the main hero's tier set only. Five tier
 * slots (a ghost for each missing piece), the piece bar marked at 2 and 4, the 2-piece and
 * 4-piece rows (lit when active), then FORGE <SLOT> (crafts the missing piece with a Sigil) and
 * Convert item (the Catalyst). Where Sigils come from lives in the (i) sheet.
 */
import type { ClassId } from '@keystone/sim';
import { type OpError, type Profile, type TierSlot, CATALYST_FANGSTEEL } from '@keystone/progression';
import '../kmmo/kmmo.css';
import { RESOURCE_ICON, attachHoverCard, classCrest, gearCard, gearSlot, hideHoverCard, iconUrl, kmModal, kmToast, panel, primaryButton, secondaryButton } from '../kmmo';
import type { MetaScreenContext } from '../context';
import { el } from '../ui/dom';
import { SLOT_LABEL } from '../ui/itemText';
import { catalystOptions, setTracker } from './setsModel';
import { opErrorText } from './upgradeModel';
import './altarForge.css';

/** The Sets rail dot: a Sigil is held and the main hero's set still misses a piece. */
export function setsAttention(p: Profile): boolean {
  if (p.wallet.sigils < 1) return false;
  const v = setTracker(p, p.mainClassId, undefined);
  return !!v && v.pieces < 5;
}

/** Mount the Sets view into the Forge body. `onForge` switches to Craft with that slot + Sigil. */
export function renderSets(ctx: MetaScreenContext, host: HTMLElement, o: { onForge(slot: TierSlot): void }): { dispose(): void } {
  const store = ctx.store;
  const root = el('div', 'kaf-sets', host);

  function render(): void {
    hideHoverCard();
    root.replaceChildren();
    const p = store.profile;
    const classId = p.mainClassId;
    const v = setTracker(p, classId, ctx.content.class(classId));
    if (!v) {
      el('div', 'kaf-wait-text', root, 'This hero has no tier set yet.');
      return;
    }
    const card = panel({ className: 'kaf-set-card' }, root);
    const head = el('div', 'kaf-set-head', card.body);
    classCrest(classId, { size: 'md' }, head);
    const t = el('div', 'kaf-set-titles', head);
    el('div', 'kaf-set-name', t, v.name);
    const bar = el('div', 'kaf-set-bar', t);
    for (let i = 0; i < 5; i++) el('span', `${i < v.pieces ? 'on' : ''}${i === 1 || i === 3 ? ' mark' : ''}`, bar);
    el('div', 'kaf-set-count km-num', head, `${v.pieces}/5`);

    const slots = el('div', 'kaf-set-slots', card.body);
    for (const s of v.slots) {
      if (s.isPiece && s.item) {
        const it = s.item;
        const h = gearSlot(it, { size: 'lg', caption: SLOT_LABEL[s.slot], equipped: true }, slots);
        attachHoverCard(h.slot, () => gearCard(it, { equippedSetCount: v.pieces }));
      } else {
        const h = gearSlot(null, { slot: s.slot, size: 'lg', caption: SLOT_LABEL[s.slot], captionTone: 'bad', onClick: () => o.onForge(s.slot) }, slots);
        h.slot.title = `Forge ${s.pieceName}`;
        h.slot.classList.add('kaf-ghost');
      }
    }

    const bonuses = el('div', 'kaf-set-bonuses', card.body);
    for (const b of v.bonuses) {
      const row = el('div', `kaf-bonus${b.active ? ' on' : ''}`, bonuses);
      el('div', 'kaf-bonus-pc km-num', row, String(b.pieces));
      const txt = el('div', 'kaf-bonus-text', row);
      const n = el('div', 'kaf-bonus-name', txt, b.name);
      el('span', 'kaf-bonus-status', n, b.active ? 'Active' : b.status);
      el('div', 'kaf-bonus-desc', txt, b.description);
    }

    const foot = el('div', 'kaf-set-foot', card.body);
    const sig = el('div', 'kaf-set-sigils', foot);
    const img = el('img', 'kaf-set-sigil-img', sig);
    img.src = iconUrl(RESOURCE_ICON['sigils']!, true);
    img.alt = '';
    el('span', 'km-num', sig, String(p.wallet.sigils));
    el('span', 'kaf-set-sigils-label', sig, p.wallet.sigils === 1 ? 'Sigil' : 'Sigils');
    const missing = v.slots.find((s) => !s.isPiece && !s.item?.legendaryId) ?? v.slots.find((s) => !s.isPiece);
    if (missing) {
      primaryButton(`Forge ${SLOT_LABEL[missing.slot]}`, {
        size: 'lg',
        cost: { icon: iconUrl(RESOURCE_ICON['sigils']!, true), amount: 1 },
        disabled: p.wallet.sigils < 1,
        title: p.wallet.sigils ? `Craft ${missing.pieceName} with an Altar Sigil` : 'Earn an Altar Sigil first (see (i))',
        onClick: () => o.onForge(missing.slot),
      }, foot);
    }
    const cat = catalystOptions(p, classId);
    if (cat.length) secondaryButton('Convert item', { size: 'lg', className: 'kaf-sec-lg', disabled: p.wallet.sigils < 1, title: `1 Sigil + ${CATALYST_FANGSTEEL} Fangsteel: turn an item into a set piece`, onClick: () => convertSheet(ctx, classId) }, foot);
  }

  render();
  const unsub = store.subscribe(render);
  return {
    dispose() {
      unsub();
      hideHoverCard();
    },
  };
}

/** Catalyst: pick a worn or bagged tier-slot item; it becomes a set piece with its stats kept. */
function convertSheet(ctx: MetaScreenContext, classId: ClassId): void {
  const m = kmModal('Convert item', { wide: true });
  el('p', 'km-modal-text', m.body, `1 Altar Sigil + ${CATALYST_FANGSTEEL} Fangsteel. Keeps item level, rarity, stats, gems and enhancement.`);
  const grid = el('div', 'kaf-convert', m.body);
  let picked: string | null = null;
  const go = primaryButton('Convert', {
    cost: { icon: iconUrl(RESOURCE_ICON['sigils']!, true), amount: 1 },
    disabled: true,
    onClick: () => {
      if (!picked) return;
      void ctx.store.act('catalyze', { itemId: picked, classId }).then((r) => {
        if (!r.ok) kmToast(opErrorText(r.reason as OpError), 'error');
        else {
          kmToast('Converted into a set piece!', 'success', 1800);
          m.close();
        }
      });
    },
  }, m.footer);
  const handles: { id: string; set(on: boolean): void }[] = [];
  for (const opt of catalystOptions(ctx.store.profile, classId)) {
    const h = gearSlot(opt.item, {
      size: 'md',
      equipped: opt.equipped,
      caption: SLOT_LABEL[opt.item.slot],
      onClick: () => {
        picked = opt.item.id;
        for (const x of handles) x.set(x.id === picked);
        go.disabled = !opt.enabled;
        go.title = opt.reason ?? '';
      },
    }, grid);
    handles.push({ id: opt.item.id, set: (on) => h.setSelected(on) });
    attachHoverCard(h.slot, () => gearCard(opt.item));
  }
  m.el.addEventListener('pointerleave', () => hideHoverCard());
}
